// StoreSelect.tsx
import React, { useState, useEffect } from 'react';
import Select from '../../components/common/Select';
import { storesAPI } from '../../api/stores';
import type { SelectOption, Store } from '../../types';

interface StoreSelectProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    label?: string;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    filter?: 'active' | 'all';
    note?: string;
    stores: Store[];
    loading?: boolean;
    isCreateMode?: boolean;
    name?: string;
}

const StoreSelect: React.FC<StoreSelectProps> = ({
    value,
    onChange,
    placeholder = 'Select a store...',
    label = 'Store',
    error,
    required = false,
    disabled = false,
    className = '',
    filter = 'active',
    note,
    stores,
    loading,
    isCreateMode = false,
    name,
}) => {
    const [fetchError, setFetchError] = useState<string | null>(null);
    const selectedStore = stores.find(s => String(s.id) === String(value));
    const activeStores = stores.filter(s => s.is_active);
    const inactiveNote = selectedStore && !selectedStore.is_active
        ? 'This store is inactive. Reactivate it to make this product available.'
        : undefined;

    const displayPlaceholder = loading
        ? 'Loading stores...'
        : stores?.length === 0
            ? 'No stores available'
            : placeholder;
    const displayNote = inactiveNote
        ? undefined
        : (note || ((activeStores.length === 1 && isCreateMode)? "You only have one active store" : undefined));
    const displayError = fetchError || error;

    const options = stores.map(store => ({
        value: store.id,
        label: store.name,
        description: store.verification_status ?? undefined,
        disabled: !store.is_active || store.verification_status != 'approved',
    }));
    
    return (
        <Select
            value={value}
            onChange={onChange}
            options={options}
            placeholder={displayPlaceholder}
            label={label}
            error={displayError}
            required={required}
            disabled={disabled || loading || stores.length === 0}
            className={className}
            note={displayNote}
            warning={inactiveNote}
            name={name}
        />
    );
};

export default StoreSelect;